<template>
  <div class="exercise-session">
    <div v-if="state === 'loading'" class="exercise-session__state" role="status">Cargando ejercicio…</div>
    <ToolAccessState v-else-if="state === 'restricted'" title="Ejercicio no disponible" message="Tu plan no incluye este ejercicio." />
    <section v-else-if="state === 'error'" class="exercise-session__state" role="alert">
      <h1>No pudimos abrir el ejercicio</h1>
      <p>{{ errorMessage }}</p>
      <div><button type="button" @click="load">Reintentar</button><router-link :to="{ name: 'ExercisesNext' }">Volver</router-link></div>
    </section>
    <main v-else-if="item" class="exercise-session__workspace">
      <header class="exercise-session__header">
        <router-link :to="{ name: 'ExercisesNext' }" aria-label="Volver a ejercicios">← <span>Ejercicios</span></router-link>
        <div class="exercise-session__identity">
          <span>{{ item.title }}</span>
          <strong>Pregunta {{ item.position + 1 }} de {{ item.total }}</strong>
        </div>
        <button v-if="item.submissionMode === 'batch' && item.position > 0" type="button" @click="goTo(item.position - 1)">Anterior</button>
      </header>
      <div class="exercise-session__progress" role="progressbar" :aria-valuenow="item.position + 1" aria-valuemin="1" :aria-valuemax="item.total">
        <span :style="{ width: `${((item.position + 1) * 100) / item.total}%` }"></span>
      </div>

      <article class="exercise-session__card">
        <div class="exercise-session__prompt">
          <span>{{ item.instructions }}</span>
          <h1>{{ item.question.title }}</h1>
          <img v-if="item.question.picture" :src="pictureUrl" :alt="item.question.title">
        </div>

        <component :is="renderer" v-model="answer" v-bind="rendererProperties" />

        <section v-if="result" class="exercise-feedback" :class="{ 'is-success': result.passed }" role="status" aria-live="polite">
          <strong>{{ result.passed ? 'Buen trabajo' : 'Revisa tu respuesta' }}</strong>
          <p>{{ result.feedback }}</p>
          <p v-if="result.scorePercent !== null">Resultado: <strong>{{ result.scorePercent }}%</strong></p>
          <ul v-if="result.review.length">
            <li v-for="row in result.review" :key="row.position">
              <span>{{ row.title }}</span>
              <strong>{{ row.correctOption && row.correctOption.text }}</strong>
            </li>
          </ul>
        </section>
      </article>

      <footer class="exercise-session__actions">
        <span v-if="validationMessage" role="alert">{{ validationMessage }}</span>
        <button v-if="result && result.passed" class="exercise-action exercise-action--primary" type="button" @click="continueAfterSuccess">
          {{ result.completed ? 'Volver a ejercicios' : 'Siguiente pregunta' }}
        </button>
        <button v-else class="exercise-action exercise-action--primary" type="button" :disabled="submitting" @click="primaryAction">
          {{ actionLabel }}
        </button>
      </footer>
    </main>
  </div>
</template>

<script>
import ToolAccessState from '@/shared/components/common/ToolAccessState.vue';
import { exercisesRepository } from '../api/exercisesRepository';
import { rendererForKind, rendererProps } from '../components/exerciseRenderers';
import { answerIsComplete, buildBatchAttempt, buildSingleAttempt, emptyAnswer, sessionStorageKey } from '../models/exerciseSession';

export default {
  name: 'ExerciseSessionPage',
  components: { ToolAccessState },
  data: () => ({
    state: 'loading', item: null, answer: {}, result: null, errorMessage: '', validationMessage: '',
    submitting: false, elapsedSeconds: 0, timer: null, answersByPosition: {}, elapsedByPosition: {}
  }),
  computed: {
    renderer() { return this.item ? rendererForKind(this.item.kind) : null; },
    rendererProperties() { return this.item ? rendererProps(this.item) : {}; },
    pictureUrl() {
      const path = this.item.question.picture;
      if (!path || /^https?:\/\//.test(path)) return path;
      return `${process.env.VUE_APP_API || ''}${path.startsWith('/') ? '' : '/'}${path}`;
    },
    actionLabel() {
      if (this.submitting) return 'Comprobando…';
      if (this.item && this.item.submissionMode === 'batch') return this.item.position === this.item.total - 1 ? 'Entregar test' : 'Siguiente';
      return this.result ? 'Volver a intentarlo' : 'Comprobar respuesta';
    }
  },
  watch: {
    '$route.fullPath'() { this.load(); }
  },
  created() { this.load(); },
  mounted() {
    this.timer = window.setInterval(() => { if (!document.hidden && this.state === 'ready') this.elapsedSeconds += 1; }, 1000);
  },
  beforeDestroy() { window.clearInterval(this.timer); },
  methods: {
    async load() {
      this.state = 'loading';
      this.result = null;
      this.validationMessage = '';
      const position = Math.max(0, Number.parseInt(this.$route.query.position, 10) || 0);
      try {
        this.item = await exercisesRepository.getItem(this.$route.params.type, this.$route.params.subtype, position);
        if (this.$route.query.reset === '1') {
          sessionStorage.removeItem(sessionStorageKey(this.item.type, this.item.subtype));
          this.answersByPosition = {};
          this.elapsedByPosition = {};
        } else {
          this.restoreBatch();
        }
        this.answer = this.answersByPosition[this.item.position] || emptyAnswer(this.item.kind);
        this.elapsedSeconds = 0;
        this.state = 'ready';
      } catch (error) {
        this.errorMessage = error && error.message ? error.message : 'Comprueba tu conexión e inténtalo de nuevo.';
        this.state = error && error.status === 403 ? 'restricted' : 'error';
      }
    },
    async primaryAction() {
      this.validationMessage = '';
      if (!answerIsComplete(this.item.kind, this.answer)) {
        this.validationMessage = 'Completa la respuesta antes de continuar.';
        return;
      }
      if (this.item.submissionMode === 'batch') {
        this.answersByPosition = { ...this.answersByPosition, [this.item.position]: { ...this.answer } };
        this.elapsedByPosition = { ...this.elapsedByPosition, [this.item.position]: this.elapsedSeconds };
        this.persistBatch();
        if (this.item.position < this.item.total - 1) {
          this.goTo(this.item.position + 1);
          return;
        }
        const payload = buildBatchAttempt(this.answersByPosition, this.elapsedByPosition, this.item.total);
        if (!payload) {
          this.validationMessage = 'Faltan respuestas. Usa “Anterior” para revisarlas.';
          return;
        }
        await this.submit(payload);
        return;
      }
      await this.submit(buildSingleAttempt(this.item.position, this.elapsedSeconds, this.answer));
    },
    async submit(payload) {
      this.submitting = true;
      try {
        this.result = await exercisesRepository.submit(this.item.type, this.item.subtype, payload);
        if (this.result.passed && this.item.submissionMode === 'batch') {
          sessionStorage.removeItem(sessionStorageKey(this.item.type, this.item.subtype));
        }
      } catch (error) {
        this.validationMessage = error && error.message ? error.message : 'No se pudo guardar la respuesta. Inténtalo de nuevo.';
      } finally {
        this.submitting = false;
      }
    },
    continueAfterSuccess() {
      if (this.result.completed) this.$router.push({ name: 'ExercisesNext' });
      else this.goTo(this.result.nextPosition);
    },
    goTo(position) {
      this.$router.push({
        name: 'ExercisesNextSession',
        params: { type: this.item.type, subtype: this.item.subtype },
        query: { position }
      });
    },
    persistBatch() {
      sessionStorage.setItem(sessionStorageKey(this.item.type, this.item.subtype), JSON.stringify({
        answers: this.answersByPosition,
        elapsed: this.elapsedByPosition
      }));
    },
    restoreBatch() {
      if (!this.item || this.item.submissionMode !== 'batch') return;
      try {
        const stored = JSON.parse(sessionStorage.getItem(sessionStorageKey(this.item.type, this.item.subtype)) || '{}');
        this.answersByPosition = stored.answers || {};
        this.elapsedByPosition = stored.elapsed || {};
      } catch (error) {
        this.answersByPosition = {};
        this.elapsedByPosition = {};
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.exercise-session { height: 100%; min-height: 0; box-sizing: border-box; overflow-y: auto; padding: 12px 12px 96px; font-family: 'Open Sans', sans-serif; color: var(--modern-text); }
.exercise-session__workspace { width: min(960px, 100%); margin: 0 auto; }
.exercise-session__header { display: grid; grid-template-columns: minmax(80px, 1fr) auto minmax(80px, 1fr); align-items: center; gap: 10px; min-height: 54px; }
.exercise-session__header > a { color: var(--modern-accent); text-decoration: none; font-weight: 700; }
.exercise-session__header > button { justify-self: end; min-height: 40px; border: 0; background: transparent; color: var(--modern-accent); font: inherit; font-weight: 700; cursor: pointer; }
.exercise-session__identity { display: grid; text-align: center; }
.exercise-session__identity span { max-width: 360px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 13px; font-weight: 700; }
.exercise-session__identity strong { color: var(--modern-muted); font-size: 11px; }
.exercise-session__progress { height: 5px; overflow: hidden; border-radius: 999px; background: var(--modern-border); }
.exercise-session__progress span { display: block; height: 100%; background: var(--modern-accent); transition: width .2s ease; }
.exercise-session__card { display: grid; gap: 22px; margin-top: 14px; padding: 18px; border: 1px solid var(--modern-border); border-radius: 20px; background: var(--modern-surface); box-shadow: 0 10px 30px rgba(15, 23, 42, .06); }
.exercise-session__prompt > span { color: var(--modern-accent); font-size: 12px; font-weight: 700; }
.exercise-session__prompt h1 { margin: 8px 0 0; font-size: clamp(20px, 5vw, 29px); line-height: 1.25; letter-spacing: -.02em; }
.exercise-session__prompt img { display: block; width: auto; max-width: 100%; max-height: 430px; margin: 18px auto 0; border-radius: 12px; }
.exercise-feedback { padding: 16px; border: 1px solid #e1a9b4; border-radius: 14px; background: color-mix(in srgb, #d94a61 10%, var(--modern-surface)); }
.exercise-feedback.is-success { border-color: #8bc7af; background: color-mix(in srgb, #36a779 10%, var(--modern-surface)); }
.exercise-feedback p { margin: 4px 0 0; color: var(--modern-muted); }
.exercise-feedback ul { display: grid; gap: 10px; margin: 14px 0 0; padding: 0; list-style: none; }
.exercise-feedback li { display: grid; gap: 3px; padding-top: 10px; border-top: 1px solid var(--modern-border); }
.exercise-feedback li strong { color: var(--modern-accent); }
.exercise-session__actions { position: fixed; right: 0; bottom: 0; left: 0; z-index: 20; display: flex; align-items: center; justify-content: flex-end; gap: 16px; min-height: 76px; box-sizing: border-box; padding: 12px max(16px, calc((100vw - 960px) / 2)); padding-bottom: max(12px, env(safe-area-inset-bottom)); border-top: 1px solid var(--modern-border); background: color-mix(in srgb, var(--modern-surface) 95%, transparent); backdrop-filter: blur(12px); }
.exercise-session__actions > span { margin-right: auto; color: #c53d56; font-size: 13px; }
.exercise-action { min-height: 48px; padding: 0 22px; border: 0; border-radius: 12px; font: inherit; font-weight: 800; cursor: pointer; }
.exercise-action--primary { background: var(--modern-accent); color: #fff; }
.exercise-action:disabled { cursor: wait; opacity: .65; }
.exercise-session__state { display: grid; place-items: center; align-content: center; min-height: 65vh; text-align: center; }
.exercise-session__state p { color: var(--modern-muted); }
.exercise-session__state button, .exercise-session__state a { display: inline-grid; place-items: center; min-height: 44px; padding: 0 16px; border: 0; border-radius: 11px; background: var(--modern-accent); color: #fff; font: inherit; text-decoration: none; cursor: pointer; }
@media (min-width: 700px) { .exercise-session { padding: 18px 24px 110px; } .exercise-session__card { padding: 28px; } }
@media (max-width: 560px) { .exercise-session__header { grid-template-columns: 48px 1fr 70px; } .exercise-session__header > a span { display: none; } .exercise-session__actions { display: grid; gap: 7px; } .exercise-session__actions > span { margin: 0; } .exercise-action { width: 100%; } }
</style>
